import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Package, Send, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import StaffIdInput from '@/components/StaffIdInput';

const MarkerWriting = () => {
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffIdLocked, setStaffIdLocked] = useState(false);
  const [bundleCode, setBundleCode] = useState('');
  // scannedBundles: [{ barcode, qty }]
  const [scannedBundles, setScannedBundles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [staffStats, setStaffStats] = useState({ today_count: 0, month_count: 0 });

  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);
  const firstWeightRef = useRef(null);

  useEffect(() => { staffInputRef.current?.focus(); }, []);
  useEffect(() => { if (staffIdLocked && staffId) loadStaffStats(); }, [staffIdLocked, staffId]);

  const loadStaffStats = async () => {
    try {
      const response = await apiClient.get(`/sections/greige/staff-stats/${staffId}`, {
        params: { operation_name: 'Marker Writing' }
      });
      setStaffStats({
        today_count: response.data.today_count || 0,
        month_count: response.data.month_count || 0
      });
    } catch (e) { /* non-blocking */ }
  };

  const handleStaffValidated = (result) => {
    if (result.valid) {
      setStaffId(result.staffId);
      setStaffName(result.staffName || '');
      setStaffIdLocked(true);
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    }
  };

  const handleBundleScan = async (e) => {
    e.preventDefault();
    const code = bundleCode.trim();
    if (!code) { toast.error('Please scan bundle barcode'); setBundleCode(''); bundleInputRef.current?.focus(); return; }
    if (scannedBundles.some(b => b.barcode === code)) {
      toast.error('Bundle already scanned'); setBundleCode(''); bundleInputRef.current?.focus(); return;
    }
    setIsProcessing(true);
    try {
      const response = await apiClient.post(`/sections/greige/marker-writing/validate-bundle?bundle_barcode=${encodeURIComponent(code)}`);
      if (response.data.valid) {
        setScannedBundles(prev => [{ barcode: code, weight: '', qty: '', color_name: response.data.color_name || '', sort_no: response.data.sort_no || '', product_type: response.data.product_type || 'dhoti' }, ...prev]);
        toast.success(`Bundle added — enter weight & qty. Total: ${scannedBundles.length + 1}`);
      }
      setBundleCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bundle validation failed');
      setBundleCode('');
    } finally {
      setIsProcessing(false);
      setTimeout(() => firstWeightRef.current?.focus(), 60);
    }
  };

  const setQty = (barcode, value) => {
    setScannedBundles(prev => prev.map(b => b.barcode === barcode ? { ...b, qty: value } : b));
  };

  const setWeight = (barcode, value) => {
    setScannedBundles(prev => prev.map(b => b.barcode === barcode ? { ...b, weight: value } : b));
  };

  const handleRemoveBundle = (barcode) => {
    setScannedBundles(prev => prev.filter(b => b.barcode !== barcode));
  };

  const totalQty = scannedBundles.reduce((s, b) => s + (parseFloat(b.qty) || 0), 0);
  const totalWeight = scannedBundles.reduce((s, b) => s + (parseFloat(b.weight) || 0), 0);

  const handleSubmit = async () => {
    if (scannedBundles.length === 0) { toast.error('No bundles scanned'); return; }
    const invalid = scannedBundles.filter(b => !b.qty || parseFloat(b.qty) <= 0);
    if (invalid.length > 0) {
      toast.error(`Enter a valid qty (> 0) for: ${invalid.map(b => b.barcode).slice(0, 3).join(', ')}`);
      return;
    }
    const invalidW = scannedBundles.filter(b => !b.weight || parseFloat(b.weight) <= 0);
    if (invalidW.length > 0) {
      toast.error(`Enter a valid weight in Kg (> 0) for: ${invalidW.map(b => b.barcode).slice(0, 3).join(', ')}`);
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await apiClient.post('/sections/greige/marker-writing/submit', {
        staff_id: staffId,
        bundle_barcodes: scannedBundles.map(b => b.barcode),
        bundle_quantities: scannedBundles.map(b => ({
          bundle_barcode: b.barcode,
          qty: b.product_type === 'running_mtr' ? parseFloat(b.qty) : parseInt(b.qty),
          weight: parseFloat(b.weight),
        })),
      });
      toast.success(`Marker writing recorded! ${response.data.bundle_count} bundles`);
      setScannedBundles([]);
      loadStaffStats();
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/greige')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Operation 6: Marker Writing</h1>
              <p className="text-sm text-[#E8DCC8]">Greige Section — Record Marker Writing & enter weight (kg) + qty per bundle</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        <Card className="border-2 border-[#E5DFD0]">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-cyan-600" /> Staff Identification
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!staffIdLocked ? (
              <StaffIdInput inputRef={staffInputRef} onValidated={handleStaffValidated} autoFocus={true} label="" />
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-cyan-600 text-white px-4 py-2 rounded-lg font-mono text-lg">{staffId}</div>
                  {staffName && <span className="text-[#5A5F6E]">{staffName}</span>}
                  <div className="text-sm text-[#5A5F6E]">
                    <span className="font-semibold text-cyan-600">{staffStats.today_count}</span> today {' · '}
                    <span className="font-semibold text-cyan-600">{staffStats.month_count}</span> this month
                  </div>
                </div>
                <Button variant="outline" onClick={() => { setStaffIdLocked(false); setStaffId(''); setStaffName(''); setScannedBundles([]); }}>Change</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {staffIdLocked && (
          <Card className="border-2 border-cyan-600">
            <CardHeader className="pb-3 bg-cyan-50">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="w-5 h-5 text-cyan-600" /> Scan Bundle Barcodes
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleBundleScan} className="flex gap-3">
                <Input ref={bundleInputRef} value={bundleCode} onChange={(e) => setBundleCode(e.target.value)} placeholder="Scan Bundle QR code..." className="text-lg h-14 font-mono" disabled={isProcessing} autoComplete="off" data-testid="marker-bundle-input" />
                <Button type="submit" className="h-14 px-8 bg-cyan-600 hover:bg-cyan-700" disabled={isProcessing} data-testid="marker-add-btn">
                  <Package className="w-5 h-5 mr-2" /> Add
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {staffIdLocked && scannedBundles.length > 0 && (
          <Card className="border-2 border-[#E5DFD0]">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Scanned Bundles ({scannedBundles.length})</CardTitle>
                  <CardDescription>Enter weight (kg) & qty for each bundle • Total: <span className="font-semibold text-cyan-700" data-testid="marker-total-qty">{totalQty}</span> pcs · <span className="font-semibold text-cyan-700" data-testid="marker-total-weight">{totalWeight.toFixed(2)}</span> kg</CardDescription>
                </div>
                <Button onClick={handleSubmit} disabled={isSubmitting} className="bg-cyan-600 hover:bg-cyan-700" data-testid="marker-submit-btn">
                  {isSubmitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Submit
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {scannedBundles.map((b, index) => (
                  <div key={b.barcode} className="flex items-center justify-between gap-3 p-3 bg-[#F5F1E8] rounded-lg" data-testid={`marker-row-${index}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm text-[#5A5F6E] w-6">#{scannedBundles.length - index}</span>
                      <div className="min-w-0">
                        <p className="font-mono font-semibold text-[#1A1F2E] truncate">{b.barcode}</p>
                        <div className="flex flex-wrap items-center gap-1 mt-0.5">
                          {b.color_name && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800" data-testid={`marker-color-${index}`}>
                              Color: {b.color_name}
                            </span>
                          )}
                          {b.product_type === 'running_mtr' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800" data-testid={`marker-runningmtr-${index}`}>
                              Running Mtr · 1 USN
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Input
                        ref={index === 0 ? firstWeightRef : null}
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={b.weight}
                        onChange={(e) => setWeight(b.barcode, e.target.value)}
                        placeholder="Weight"
                        className="w-24 h-10 text-center font-semibold"
                        data-testid={`marker-weight-${index}`}
                      />
                      <span className="text-xs text-[#5A5F6E] w-8">kg</span>
                      <Input
                        type="number"
                        min={b.product_type === 'running_mtr' ? '0.01' : '1'}
                        step={b.product_type === 'running_mtr' ? '0.01' : '1'}
                        value={b.qty}
                        onChange={(e) => setQty(b.barcode, e.target.value)}
                        placeholder={b.product_type === 'running_mtr' ? 'Meters' : 'Qty'}
                        className="w-24 h-10 text-center font-semibold"
                        data-testid={`marker-qty-${index}`}
                      />
                      <span className="text-xs text-[#5A5F6E] w-8" data-testid={`marker-unit-${index}`}>{b.product_type === 'running_mtr' ? 'm' : 'pcs'}</span>
                      <Button variant="ghost" size="icon" onClick={() => handleRemoveBundle(b.barcode)} className="text-red-500 hover:bg-red-50" data-testid={`marker-remove-${index}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default MarkerWriting;
